import {
  Box,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Radio as MuiRadio,
} from "@mui/material";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Label } from "@/components/Label";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import { Checkbox, CheckboxGroup } from "@/components/Checkbox";
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormSetValue,
} from "react-hook-form";
import { MultiSelect } from "@/components/Select";
import { type ISelectObject } from "@/types/select";
import { type FormData } from "../types/formTypes";
import { RangeInput } from "./RangeInput";
import { AreaUsePurposeDataSourceToggle } from "@/components/Toggle";

// 不動産種別のマップ
export const REAL_ESTATE_TYPE_MAP = {
  land: { label: "土地", value: "land" },
  kubunBuilding: { label: "区分建物", value: "kubun_building" },
  building: { label: "建物", value: "building" },
};

// 容積率で入力可能な最小値、最小値を定義する。
const YOUSEKI_RATIO_MIN = 50;
const YOUSEKI_RATIO_MAX = 1300;
// 建ぺい率で入力可能な最小値、最小値を定義する。
const KENPEI_RATIO_MIN = 30;
const KENPEI_RATIO_MAX = 80;

interface LandSearchAccordionProps {
  control: Control<FormData>;
  watchedRealEstateType: string;
  setValue: UseFormSetValue<FormData>;
  formState: {
    errors: FieldErrors<FormData>;
  };
  areaUsePurposeOptions: ISelectObject[];
  isParkingLotInputFilled: boolean;
  cityPlanningAreaUsePurposeSearch?: boolean;
}

export const LandSearchAccordion: React.FC<LandSearchAccordionProps> = ({
  control,
  watchedRealEstateType,
  setValue,
  formState,
  isParkingLotInputFilled,
  areaUsePurposeOptions,
  cityPlanningAreaUsePurposeSearch = false,
}) => {
  return (
    <Accordion
      expanded={watchedRealEstateType === REAL_ESTATE_TYPE_MAP.land.value}
      onChange={(_, _expanded) => {
        setValue("realEstateType", REAL_ESTATE_TYPE_MAP.land.value, {
          shouldValidate: true,
        });
      }}
      square
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        py: 1,
        px: 2,
        mt: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Box
          component="label"
          sx={{ display: "flex", alignItems: "center", width: "100%" }}
        >
          <MuiRadio
            checked={watchedRealEstateType === REAL_ESTATE_TYPE_MAP.land.value}
            onChange={() => {
              setValue("realEstateType", REAL_ESTATE_TYPE_MAP.land.value, {
                shouldValidate: true,
              });
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            <Box sx={{ flex: "0 0 auto", mr: 5 }}>
              <Typography variant="subtitle1" sx={{ color: "text.primary" }}>
                土地の条件から探す
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: "left" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                土地の種別、用途地域、推計地積など
                <br />
                土地に関連する内容を検索条件に含めることができます
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Box sx={{ mx: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Grid container sx={{ top: "50%" }}>
            <Grid item xs={12}>
              <CheckboxGroup label="土地種別">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Controller
                    control={control}
                    name={"landType"}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <>
                          <Checkbox
                            label="農地"
                            checked={value.farm}
                            onChange={(checked) => {
                              onChange({ ...value, farm: checked });
                            }}
                          />
                          <Checkbox
                            label="太陽光"
                            checked={value.solar}
                            onChange={(checked) => {
                              onChange({ ...value, solar: checked });
                            }}
                          />
                          <Checkbox
                            label="駐車場"
                            checked={value.parkingLot}
                            onChange={(checked) => {
                              onChange({ ...value, parkingLot: checked });
                            }}
                            disabled={isParkingLotInputFilled}
                          />
                        </>
                      );
                    }}
                  />
                </Box>
              </CheckboxGroup>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <Label>駐車場名（部分一致）</Label>
          <Controller
            control={control}
            name="parkingLotName"
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  placeholder="駐車場名"
                  sx={{ width: "250px" }}
                  variant="outlined"
                />
              );
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="totalParkingLotArea"
            label="駐車場 推計地積"
            unit="㎡"
            errors={formState.errors}
            inputProps={{ min: 0, type: "number", step: "10" }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Label>用途地域</Label>
            {cityPlanningAreaUsePurposeSearch && (
              <Controller
                control={control}
                name="areaUsePurposeDataSource"
                render={({ field: { value, onChange } }) => (
                  <AreaUsePurposeDataSourceToggle
                    value={value}
                    onChange={(_event, newValue) => {
                      if (newValue !== null) {
                        onChange(newValue);
                      }
                    }}
                  />
                )}
              />
            )}
          </Box>
          <Grid sx={{ display: "inline-block", top: "50%", mr: 1 }}>
            <Controller
              control={control}
              name="landAreaUsePurpose"
              render={({ field: { value, onChange }, fieldState }) => {
                return (
                  <MultiSelect
                    error={Boolean(fieldState.error)}
                    formHelperText={fieldState.error?.message}
                    label="用途地域"
                    value={value.map((v) => v.toString())}
                    onChange={(vals) => {
                      onChange(vals.map((v) => Number(v)));
                    }}
                    options={areaUsePurposeOptions}
                    maxSelect={5}
                  />
                );
              }}
            />
          </Grid>
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="landYousekiRatio"
            label="容積率"
            unit="%"
            errors={formState.errors}
            inputProps={{
              min: YOUSEKI_RATIO_MIN,
              max: YOUSEKI_RATIO_MAX,
              type: "number",
              step: "10",
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="landKenpeiRatio"
            label="建ぺい率"
            unit="%"
            errors={formState.errors}
            inputProps={{
              min: KENPEI_RATIO_MIN,
              max: KENPEI_RATIO_MAX,
              type: "number",
              step: "10",
            }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="estimateAcreage"
            label="推計地積"
            unit="㎡"
            errors={formState.errors}
            inputProps={{ min: 0, type: "number", step: "10" }}
          />
        </Box>
        <Box sx={{ mx: 2, mb: 2 }}>
          <RangeInput
            control={control}
            name="landPrice"
            label="公示価格"
            unit="円/㎡"
            errors={formState.errors}
            inputProps={{ min: 0, type: "number", step: "10" }}
          />
        </Box>
        <Box
          sx={{
            mx: 2,
            mb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Grid sx={{ display: "block" }}>
            <Label>最寄駅からの徒歩時間</Label>
            <Controller
              name={"landWalk"}
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Select
                    value={value === null ? -1 : value}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    sx={{ width: "250px", minHeight: "56px" }}
                    variant="outlined"
                  >
                    <MenuItem key={-1} value={-1}>
                      指定なし
                    </MenuItem>
                    {[...Array(60).keys()]
                      .filter((val) => val >= 1)
                      .map((val) => (
                        <MenuItem key={val} value={val}>
                          {val}分以内
                        </MenuItem>
                      ))}
                    <MenuItem key={60} value={60}>
                      60分以上
                    </MenuItem>
                  </Select>
                );
              }}
            />
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
